import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    this.addEventListener('pointerdown', this.#loadDeferredMedia, { signal, passive: true, capture: true });
    this.addEventListener('keydown', this.#loadDeferredMedia, { signal, capture: true });
    this.addEventListener('focusin', this.#loadDeferredMedia, { signal });
    this.#mediaObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'] });
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  #hydrateImages = (root) => {
    root.querySelectorAll('img[data-bella-src]').forEach((image) => {
      image.loading = 'eager';
      if (image.dataset.bellaSrcset) image.srcset = image.dataset.bellaSrcset;
      image.src = image.dataset.bellaSrc;
      delete image.dataset.bellaSrc;
      delete image.dataset.bellaSrcset;
    });
  };

  #loadDeferredMedia = () => this.#hydrateImages(this);

  #mediaObserver = new MutationObserver((changes) => {
    for (const change of changes) {
      if (change.target.getAttribute('aria-hidden') === 'false') this.#hydrateImages(change.target);
    }
  });

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#mediaObserver.disconnect();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
